import { Article as AnswersArticle } from '@wix/answers-api';
import type {
  ArticleData,
  HomepageContent,
} from '@wix/wix-help-widget-common/homepage';
import isString from 'lodash/isString';

import { CategoryItemType } from '../../common/api/getCategories';
import { SearchArticlesItem } from '../../common/api/searchArticles';
import { clientSdk } from '../../common/ClientSDK';
import { SEARCH_LIMIT } from '../../common/reducers/solutions';
import { TSolution } from '../../common/reducers/types';
import { helpCenterService } from '../../common/services/helpCenter';
import { homepageService } from '../../common/services/homepage/homepageService';
import { renderModel } from '../renderModel';
import {
  biEmptySearchResults,
  biWidgetInteractionContent,
  biWidgetInteractionHeader,
  biWidgetPageEngagement,
  biWidgetPresentedContent,
  chatbotNotificationReceived,
  clickOnSolution,
  helpCenterBreadcrumbsScrolled,
} from './bi';
import { getHomepageSolutionInfo } from './getHomepageSolutionInfo';
import { locationToSolutionInfo } from './locationToSolutionInfo';
import { getTestVersionPayload } from './testVersionPayload';
import {
  KB_WIDGET_STATE,
  KbWidgetStateBI,
  SolutionInfo,
  SolutionTypes,
} from './types';
import { getMaxScrollData } from './utils';

const { userId, locale } = renderModel.get();
const PAGE_ENGAGEMENT_INTERVAL = 10 * 1000;

export enum BlockId {
  SearchResults = 1,
  SingleArticle,
  EmptyHomePage,
  AllHelpTopics,
  SubCategory,
  SolutionList,
  Homepage,
  RecommendedArticle,
  HelpCenter,
  ContactUsButton,
  ChatWithUs,
  HirePartnerButton,
  UserContacts,
  StatusPage,
  Resources,
  Breadcrumbs,
  ReportABugButton,
  RequestSuccessManager,
}

export enum PresentedState {
  SearchResults = 'Search Results',
  SingleArticle = 'Single Article',
  EmptyHomePage = 'Empty Home Page',
  Chatbot = 'Chatbot',
  AllHelpTopics = 'All Help Topics',
  SubCategoriesList = 'Sub Categories List',
  SolutionList = 'Solution List',
  Homepage = 'Homepage',
  WidgetButton = 'Widget Button',
}

export enum BlockType {
  Solution = 'Solution',
  CategoryList = 'category_list',
  SubCategoryList = 'sub_category_list',
  SolutionList = 'solution_list',
  Link = 'link',
  Button = 'button',
  Tab = 'tab',
  Breadcrumbs = 'Breadcrumbs',
  // There will be other values like "Button" etc.
}

export enum SupportType {
  Article = 'Article',
  Category = 'category',
  SubCategory = 'sub_category',
  Link = 'link',
  Button = 'button',
  AllHelpTopics = 'all_help_topics',
  // There will be other values like "Videos", "FAQ" etc.
}

export enum ButtonClicked {
  SolutionClicked = 'solution_clicked',
  SolutionDeepLink = 'article_internal_link',
  SolutionLinkInternal = 'Solution Link Internal',
  Back = 'back',
  OpenArticleInHC = 'open_in_help_center',
  RecommendedArticle = 'recommended_article',
  ExpandArticleSection = 'article_section_expend',
  CollapseArticleSection = 'article_section_collapse',
  ArticleExternalLink = 'article_external_link',
  ArticleTabSelection = 'tab_selection',
  // There will be other values like "Videos", "FAQ" etc.
  ExpandChatbot = 'chatbot_expand',
  CollapseChatbot = 'chatbot_collapse',
  CategorySelection = 'category_selection',
  SubCategorySelection = 'sub_category_selection',
  SearchBarTextTyping = 'search_bar_text_typing',
  ClearSearchBar = 'clear_search_bar',
  HelpCenter = 'help_center',
  MoreHelpResources = 'more_help_resources',
  HirePartner = 'hire_a_partner',
  ContactUs = 'contact_us',
  ChatWithUs = 'chat_with_us',
  StatusPage = 'status_page',
  Breadcrumbs = 'breadcrumbs',
  CloseKbWidget = 'close_kb_widget',
  Notifications = 'chatbot_notifications',
  OpenEmail = 'open_an_email',
  CopyEmail = 'copy_email_address',
  ShareFeedback = 'share_your_feedback',
}

export enum ActivityType {
  Scroll = 'scroll',
  ScrollToBottom = 'scroll_to_bottom',
  MouseClick = 'mouse_click',
  MouseMove = 'mouse_move',
  ScrollAddedContent = 'scroll_added_content',
}

const initialActivities = {
  [ActivityType.Scroll]: false,
  [ActivityType.ScrollToBottom]: false,
  [ActivityType.MouseClick]: false,
  [ActivityType.MouseMove]: false,
  [ActivityType.ScrollAddedContent]: false,
};

export const blockNames: Record<BlockId, string> = {
  [BlockId.SearchResults]: 'Search Results',
  [BlockId.SingleArticle]: 'Single Article',
  [BlockId.EmptyHomePage]: 'Empty Home Page',
  [BlockId.AllHelpTopics]: 'All Help Topics',
  [BlockId.SubCategory]: 'Sub Category',
  [BlockId.SolutionList]: 'Solution List',
  [BlockId.Homepage]: 'Homepage',
  [BlockId.RecommendedArticle]: 'Recommended Article',
  [BlockId.HelpCenter]: 'Help Center',
  [BlockId.ContactUsButton]: 'Contact Us Button',
  [BlockId.ChatWithUs]: 'Chat With Us',
  [BlockId.HirePartnerButton]: 'Hire Partner Button',
  [BlockId.UserContacts]: 'User Contacts',
  [BlockId.StatusPage]: 'Status Page Button',
  [BlockId.Resources]: 'Resources',
  [BlockId.Breadcrumbs]: 'Breadcrumbs',
  [BlockId.ReportABugButton]: 'Report a Bug',
  [BlockId.RequestSuccessManager]: 'Request Success Manager',
};

type CommonEventsData = {
  uuid: string;
  lng: string;
  date_created: number; // timestamp
  searched_text?: string;
  last_conversation_id?: string;
  last_chatbot_node_id?: string;
  last_user_intent_id?: string;
  ticket_id?: string;
  current_presented_state?: PresentedState;
  previous_presented_state?: PresentedState;
};

export interface SolutionInfoParameters {
  blockId: BlockId;
  articleData: SearchArticlesItem[] | string | null;
  position?: number;
  text?: string;
  sectionText?: string;
  url?: string;
  isSolutionList?: boolean;
}

function getSolutionInfo({
  blockId,
  articleData,
  position = 1,
  text,
  sectionText,
  url,
  isSolutionList = false,
}: SolutionInfoParameters): SolutionInfo {
  const generalData = {
    url,
    text,
    section_text: sectionText,
    block_id: blockId,
    block_name: blockNames[blockId],
    block_position: 1, // for now only '1' is possible
    block_type: isSolutionList ? BlockType.SolutionList : BlockType.Solution,
  };

  if (blockId === BlockId.EmptyHomePage) {
    return { ...generalData, block_details: null };
  }

  if (Array.isArray(articleData)) {
    const startIndex =
      articleData.length > SEARCH_LIMIT ? articleData.length - SEARCH_LIMIT : 0;
    return {
      ...generalData,
      block_details: articleData
        // Limit solution list to SEARCH_LIMIT because large list of solutions fails requests as they are sent in a URL
        .slice(-1 * SEARCH_LIMIT)
        .map((article, index) => ({
          support_type: SupportType.Article,
          support_id: article.id,
          support_position: startIndex + index + 1, // increment value to start count from 1(not 0)
        })),
    };
  }

  return {
    ...generalData,
    block_details: [
      {
        support_type: SupportType.Article,
        support_id: articleData,
        support_position: position,
      },
    ],
  };
}

function getCategoriesSolutionInfo(data: {
  categories: CategoryItemType[];
  isSubCategory: boolean;
  position?: number;
}): SolutionInfo {
  const blockId = data.isSubCategory
    ? BlockId.SubCategory
    : BlockId.AllHelpTopics;
  return {
    block_id: blockId,
    block_name: blockNames[blockId],
    block_position: 1,
    block_type: data.isSubCategory
      ? BlockType.SubCategoryList
      : BlockType.CategoryList,
    block_details: data.categories?.map((category, i) => ({
      support_type: data.isSubCategory
        ? SupportType.SubCategory
        : SupportType.Category,
      support_id: category.id,
      support_position: (data.position || i) + 1, // increment value to start count from 1(not 0)
    })),
  };
}

export interface IBIHelpCenter {
  /* Set Only! */
  conversationId: string;
  nodeId: string;
  intentId: string;
  mainOptionName: string;
  ticketId: string;
  origPageName: string;
  origPlatform: string;
  searchedText: string | undefined;
  currentPresentedState: PresentedState;
  fullPageSidebarOpened: boolean;
  fullPageSidebarExtended: boolean;
  articleRecommendationVersion: string | undefined;

  logSingleArticlePresented(articleId: string, articleLang: string): void;
  logSearchResultsPresented(articles: SearchArticlesItem[]): void;
  logSearchResultsInteraction(articleId: string, position: number): void;
  logSolutionLinkInteraction(
    articleId: string,
    articleLang: string,
    isExternalLink: boolean,
    text: string,
    section_text: string,
  ): void;
  initPageEngagementEventInterval(params?: {
    getSideBarStateBI?: Function;
  }): void;
}

class BIHelpCenter implements IBIHelpCenter {
  private _currentPresentedState: PresentedState | undefined;
  private _previousPresentedState: PresentedState | undefined;
  private _conversationId: string | undefined;
  private _nodeId: string | undefined;
  private _intentId: string | undefined;
  private _mainOptionName: string | undefined;
  private _ticketId: string | undefined;
  private _origPageName: string | undefined;
  private _origPlatform: string | undefined;
  private _searchedText: string | undefined;
  private _searchArticlesList: SearchArticlesItem[] | undefined;
  private _categoryArticlesList: AnswersArticle[] | undefined;
  private _categoriesList: CategoryItemType[] | undefined;
  private _lastActivities = { ...initialActivities };
  private _pageEngagementEventIntervalId: number | undefined;
  private _lastArticleId: string | undefined;
  private _fullPageSidebarOpened: boolean | undefined;
  private _fullPageSidebarExtended: boolean | undefined;
  private _articleRecommendationVersion: string | undefined;

  constructor() {
    this.initPageEngagementEventInterval();
  }

  set conversationId(id: string) {
    this._conversationId = id;
  }

  set nodeId(id: string) {
    this._nodeId = id;
  }

  set intentId(id: string) {
    this._intentId = id;
  }
  set mainOptionName(name: string) {
    this._mainOptionName = name;
  }
  set ticketId(id: string) {
    this._ticketId = id;
  }

  set currentPresentedState(state: PresentedState) {
    if (this._currentPresentedState) {
      this._previousPresentedState = this._currentPresentedState;
    }

    this._currentPresentedState = state;
  }

  set previousPresentedState(state: PresentedState) {
    this._previousPresentedState = state;
  }

  set origPageName(origPageName: string) {
    this._origPageName = origPageName;
  }

  set origPlatform(origPlatform: string) {
    this._origPlatform = origPlatform;
  }

  set searchedText(searchedText: string | undefined) {
    this._searchedText = searchedText;
  }
  set searchArticlesList(articles: SearchArticlesItem[] | undefined) {
    this._searchArticlesList = articles;
  }
  get searchArticlesList() {
    return this._searchArticlesList;
  }
  set categoryArticlesList(articles: AnswersArticle[] | undefined) {
    this._categoryArticlesList = articles;
    this._categoriesList = undefined;
  }
  set categoriesList(categories: CategoryItemType[] | undefined) {
    this._categoriesList = categories;
    this._categoryArticlesList = undefined;
  }
  set fullPageSidebarOpened(value: boolean) {
    this._fullPageSidebarOpened = value;
  }
  set fullPageSidebarExtended(value: boolean) {
    this._fullPageSidebarExtended = value;
  }
  set articleRecommendationVersion(value: string | undefined) {
    this._articleRecommendationVersion = value;
  }

  logPageEngagementActivity(activity: ActivityType) {
    this._lastActivities[activity] = true;
  }

  logSingleArticlePresented(articleId: string, articleLang: string): void {
    if (this._currentPresentedState !== PresentedState.SingleArticle) {
      return;
    }
    this._lastArticleId = articleId;
    biWidgetPresentedContent({
      ...this.commonEventData,
      ...this.kbWidgetStateBI,
      article_id: articleId,
      is_chatbot_solution_click:
        this._previousPresentedState === PresentedState.Chatbot,
      lang: articleLang,
      solution_info: getSolutionInfo({
        blockId: BlockId.SingleArticle,
        articleData: articleId,
      }),
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  logSearchResultsPresented(articles: SearchArticlesItem[]): void {
    if (this._currentPresentedState !== PresentedState.SearchResults) {
      return;
    }
    biWidgetPresentedContent({
      ...this.commonEventData,
      solution_info: getSolutionInfo({
        blockId: BlockId.SearchResults,
        articleData: articles,
      }),
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  logEmptySearchResultsReceived(): void {
    if (this._currentPresentedState !== PresentedState.SearchResults) {
      return;
    }

    biEmptySearchResults({
      ...this.commonEventData,
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
    });
  }

  logSearchResultsInteraction(articleId: string, position: number): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      article_id: articleId,
      button_clicked: ButtonClicked.SolutionClicked,
      solution_info: getSolutionInfo({
        blockId: BlockId.SearchResults,
        articleData: articleId,
        position: position + 1,
      }),
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  logSearchBarUsed(): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      button_clicked: ButtonClicked.SearchBarTextTyping,
      solution_info: null,
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  logClearSearchUsed(solutions: TSolution[]): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      button_clicked: ButtonClicked.ClearSearchBar,
      solution_info: getSolutionInfo({
        blockId: BlockId.SearchResults,
        articleData: solutions,
      }),
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  logSolutionLinkInteraction(
    articleId: string,
    articleLang: string,
    isExternalLink: boolean,
    text: string,
    sectionText?: string,
  ): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      ...this.kbWidgetStateBI,
      article_id: articleId,
      lang: articleLang,
      button_clicked: isExternalLink
        ? ButtonClicked.SolutionLinkInternal
        : ButtonClicked.SolutionDeepLink,
      solution_info: getSolutionInfo({
        text,
        sectionText,
        blockId: BlockId.SingleArticle,
        articleData: articleId,
      }),
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  logChatbotSliderInteraction(
    collapse: boolean,
    pathname: string,
    search: string,
  ): void {
    const solution = locationToSolutionInfo(
      pathname,
      search,
      this._searchArticlesList!,
    );
    if (collapse) {
      biWidgetInteractionHeader({
        ...this.commonEventData,
        button_clicked: ButtonClicked.CollapseChatbot,
        solution_info: null,
        orig_page_name: this._origPageName,
        orig_platform: this._origPlatform,
      });
      return;
    }

    biWidgetInteractionHeader({
      ...this.commonEventData,
      button_clicked: ButtonClicked.ExpandChatbot,
      solution_info: getSolutionInfo(solution),
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
    });
  }

  logBackButtonOnArticleView(articleId: string): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      ...this.kbWidgetStateBI,
      button_clicked: ButtonClicked.Back,
      solution_info: getSolutionInfo({
        blockId: BlockId.SingleArticle,
        articleData: articleId,
      }),
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  logBackButtonOnChatbotView(): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      button_clicked: ButtonClicked.Back,
      solution_info: [],
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
    });
  }

  logOpenRecommendedArticleInHC(articleId: string, articleIndex: number): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      button_clicked: ButtonClicked.RecommendedArticle,
      solution_info: getSolutionInfo({
        blockId: BlockId.SingleArticle,
        articleData: articleId,
        position: articleIndex + 1,
      }),
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  logOpenArticleInHC(articleId: string | undefined): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      button_clicked: ButtonClicked.OpenArticleInHC,
      solution_info: getSolutionInfo({
        blockId: BlockId.SingleArticle,
        articleData: articleId ?? null,
      }),
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  logBackButtonOnArticlesListView(articles: SearchArticlesItem[]): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      button_clicked: ButtonClicked.Back,
      solution_info: getSolutionInfo({
        blockId: BlockId.SearchResults,
        articleData: articles,
      }),
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  logSectionInteraction(
    expand: boolean,
    text: string,
    articleId: string,
  ): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      ...this.kbWidgetStateBI,
      button_clicked: expand
        ? ButtonClicked.ExpandArticleSection
        : ButtonClicked.CollapseArticleSection,
      solution_info: getSolutionInfo({
        blockId: BlockId.SingleArticle,
        articleData: articleId,
        text,
      }),
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  logCloseFullPageArticleSidebar(text: string, articleId: string): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      ...this.kbWidgetStateBI,
      button_clicked: ButtonClicked.CloseKbWidget,
      solution_info: getSolutionInfo({
        blockId: BlockId.SingleArticle,
        articleData: articleId,
        text,
      }),
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  logArticleExternalLink(articleId: string, text: string, url: string): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      ...this.kbWidgetStateBI,
      button_clicked: ButtonClicked.ArticleExternalLink,
      solution_info: getSolutionInfo({
        url,
        text,
        blockId: BlockId.SingleArticle,
        articleData: articleId,
      }),
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  logCategoriesPresented(categories: CategoryItemType[]): void {
    if (!this.isCategoriesState()) {
      return;
    }
    const isSubCategory =
      this._currentPresentedState === PresentedState.SubCategoriesList;
    biWidgetPresentedContent({
      ...this.commonEventData,
      solution_info: getCategoriesSolutionInfo({
        categories,
        isSubCategory,
      }),
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      category_id: this.currentCategoryId,
    });
  }

  logCategoriesInteraction(category: CategoryItemType, position: number): void {
    if (!this.isCategoriesState()) {
      return;
    }
    const isSubCategory =
      this._currentPresentedState === PresentedState.SubCategoriesList;
    biWidgetInteractionContent({
      ...this.commonEventData,
      button_clicked: isSubCategory
        ? ButtonClicked.SubCategorySelection
        : ButtonClicked.CategorySelection,
      solution_info: getCategoriesSolutionInfo({
        categories: [category],
        position,
        isSubCategory,
      }),
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      category_id: this.currentCategoryId,
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  logCategorySolutionListPresented(articles: AnswersArticle[]): void {
    if (this._currentPresentedState !== PresentedState.SolutionList) {
      return;
    }
    biWidgetPresentedContent({
      ...this.commonEventData,
      solution_info: getSolutionInfo({
        blockId: BlockId.SolutionList,
        articleData: articles,
        isSolutionList: true,
      }),
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      category_id: this.currentCategoryId,
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  logHomePagePresented(
    homepageContent: HomepageContent,
    recommendedArticles: ArticleData[] | null,
  ): void {
    if (this._currentPresentedState !== PresentedState.Homepage) {
      return;
    }
    biWidgetPresentedContent({
      ...this.commonEventData,
      solution_info: getHomepageSolutionInfo(
        homepageContent,
        recommendedArticles,
      ),
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  logCategorySolutionListInteraction(
    articleId: string,
    position: number,
    categoryId: string,
  ): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      button_clicked: ButtonClicked.SolutionClicked,
      solution_info: getSolutionInfo({
        blockId: BlockId.SolutionList,
        articleData: articleId,
        position: position + 1,
        isSolutionList: true,
      }),
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      category_id: categoryId,
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  logCategorySubCategoryListBackClicked(category: CategoryItemType): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      button_clicked: ButtonClicked.Back,
      solution_info: getCategoriesSolutionInfo({
        categories: category.children || [],
        isSubCategory: false,
      }),
      category_id: category.id,
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  logCategoriesListBackClicked(categories: CategoryItemType[]): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      button_clicked: ButtonClicked.Back,
      solution_info: getCategoriesSolutionInfo({
        categories: categories || [],
        isSubCategory: false,
      }),
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  logCategorySolutionListBackClicked(category: CategoryItemType): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      button_clicked: ButtonClicked.Back,
      solution_info: getSolutionInfo({
        articleData: this._categoryArticlesList || [],
        blockId: BlockId.SolutionList,
        isSolutionList: true,
      }),
      category_id: category.id,
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  replaceCurrentPresentedState(state: PresentedState): void {
    this._currentPresentedState = state;
  }

  initPageEngagementEventInterval(params?: {
    getSideBarStateBI?: Function;
  }): void {
    if (this._pageEngagementEventIntervalId) {
      clearInterval(this._pageEngagementEventIntervalId);
    }
    this._pageEngagementEventIntervalId = setInterval(async () => {
      if (
        document.hidden ||
        !(await clientSdk.isWidgetExpanded()) ||
        this._currentPresentedState === PresentedState.Chatbot
      ) {
        return;
      }
      biWidgetPageEngagement({
        ...this.commonEventData,
        ...this.kbWidgetStateBI,
        ...params?.getSideBarStateBI?.(),
        orig_page_name: this._origPageName,
        orig_platform: this._origPlatform,
        activity_type_mouse_click: this._lastActivities[
          ActivityType.MouseClick
        ],
        activity_type_mouse_move: this._lastActivities[ActivityType.MouseMove],
        activity_type_scroll: this._lastActivities[ActivityType.Scroll],
        activity_type_scroll_to_bottom: this._lastActivities[
          ActivityType.ScrollToBottom
        ],
        activity_type_widget_is_open: true,
        solution_info: {} as any,
        activity_type_scroll_added_content: this._lastActivities[
          ActivityType.ScrollAddedContent
        ],
        article_id: this.currentArticleId,
        category_id: this.currentCategoryId,
        ...getMaxScrollData(this._currentPresentedState!),
      });
      this._lastActivities = { ...initialActivities };
    }, PAGE_ENGAGEMENT_INTERVAL) as any;
  }

  logHelpCenterButtonInteraction(positionIndex: number): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      button_clicked: ButtonClicked.HelpCenter,
      orig_page_name: this.origPageName,
      orig_platform: this.origPlatform,
      solution_info: {
        block_id: BlockId.HelpCenter,
        block_name: blockNames[BlockId.HelpCenter],
        block_position: positionIndex + 1,
        block_type: BlockType.Link,
        block_details: [
          {
            support_type: SupportType.Link,
            support_position: 1,
            support_id: null,
          },
        ],
      },
    });
  }

  logChatWithUsInteraction(positionIndex: number): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      button_clicked: ButtonClicked.ChatWithUs,
      orig_page_name: this.origPageName,
      orig_platform: this.origPlatform,
      solution_info: {
        block_id: BlockId.ChatWithUs,
        block_name: blockNames[BlockId.ChatWithUs],
        block_position: positionIndex + 1,
        block_type: BlockType.Link,
        block_details: [
          {
            support_type: SupportType.Link,
            support_position: 1,
            support_id: null,
          },
        ],
      },
    });
  }

  logHelpResourcesButtonInteraction(
    url: string,
    text: string,
    itemPosition: number,
    blockPosition: number,
  ): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      button_clicked: ButtonClicked.MoreHelpResources,
      orig_page_name: this.origPageName,
      orig_platform: this.origPlatform,
      solution_info: {
        url,
        text,
        block_id: BlockId.Resources,
        block_name: blockNames[BlockId.Resources],
        block_position: blockPosition + 1,
        block_type: BlockType.Link,
        block_details: [
          {
            support_type: SupportType.Link,
            support_position: itemPosition + 1,
            support_id: null,
          },
        ],
      },
    });
  }

  logHirePartnerButtonInteraction(
    url: string,
    text: string,
    positionIndex: number,
  ): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      button_clicked: ButtonClicked.HirePartner,
      orig_page_name: this.origPageName,
      orig_platform: this.origPlatform,
      solution_info: {
        url,
        text,
        block_id: BlockId.HirePartnerButton,
        block_name: blockNames[BlockId.HirePartnerButton],
        block_position: positionIndex + 1,
        block_type: BlockType.Button,
        block_details: [
          {
            support_type: SupportType.Button,
            support_position: 1,
            support_id: null,
          },
        ],
      },
    });
  }

  logContactUsButtonInteraction(text: string, positionIndex: number): void {
    // {"url":"http://xyz","text":"contact_us","block_id":x,"block_name":"chatbot","block_position":y,"block_type":"chatbot,"block_details":[{"support_type":"link","support_id":null,"support_position":1}]}
    biWidgetInteractionContent({
      ...this.commonEventData,
      button_clicked: ButtonClicked.ContactUs,
      orig_page_name: this.origPageName,
      orig_platform: this.origPlatform,
      solution_info: {
        text,
        block_id: BlockId.ContactUsButton,
        block_name: blockNames[BlockId.ContactUsButton],
        block_position: positionIndex + 1,
        block_type: BlockType.Button,
        block_details: [
          {
            support_type: SupportType.Button,
            support_position: 1,
            support_id: null,
          },
        ],
      },
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  logStatusPageInteraction(
    url: string,
    text: string,
    positionIndex: number,
  ): void {
    // current_presented_state=widget_home_page
    // button_clicked= status_page
    // solution_info: {"url":"http://xyz","text":"status page ","block_id":x,"block_name":"status page","block_position":y,"block_type":"status page" block_details":[{"support_type":"link","support_id":null,"support_position":1}]}
    biWidgetInteractionContent({
      ...this.commonEventData,
      button_clicked: ButtonClicked.StatusPage,
      orig_page_name: this.origPageName,
      orig_platform: this.origPlatform,
      solution_info: {
        url,
        text,
        block_id: BlockId.StatusPage,
        block_name: blockNames[BlockId.StatusPage],
        block_position: positionIndex + 1,
        block_type: BlockType.Link,
        block_details: [
          {
            support_type: SupportType.Link,
            support_position: 1,
            support_id: null,
          },
        ],
      },
    });
  }

  logArticleTabInteraction(
    tabName: string,
    tabIndex: number,
    articleId: string,
  ): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      button_clicked: ButtonClicked.ArticleTabSelection,
      orig_page_name: this.origPageName,
      orig_platform: this.origPlatform,
      solution_info: getSolutionInfo({
        text: tabName,
        blockId: BlockId.SingleArticle,
        position: tabIndex,
        articleData: articleId,
      }),
      article_recommendation_version: this._articleRecommendationVersion,
    });
  }

  logHelpCenterBreadcrumbsClickInteraction(
    breadcrumbName: string,
    breadcrumbIndex: number,
    entityType: SupportType,
    entityId: string | null,
  ): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      button_clicked: ButtonClicked.Breadcrumbs,
      orig_page_name: this.origPageName,
      orig_platform: this.origPlatform,
      solution_info: {
        text: breadcrumbName,
        block_id: BlockId.Breadcrumbs,
        block_name: blockNames[BlockId.Breadcrumbs],
        block_position: 1,
        block_type: BlockType.Breadcrumbs,
        block_details: [
          {
            support_type: entityType,
            support_id: entityId,
            support_position: breadcrumbIndex + 1,
          },
        ],
      },
    });
  }

  logBreadcrumbsScrolledInteraction(
    percent_scrolled: number,
    scroll_direction: string,
  ): void {
    helpCenterBreadcrumbsScrolled({
      ...this.commonEventData,
      percent_scrolled,
      scroll_direction,
    });
  }

  logChatbotNotificationReceived = (counter_value: number) => {
    chatbotNotificationReceived({
      ...this.commonEventData,
      counter_value,
    });
  };

  logHomepageHeaderButtonClicked(
    button_clicked: ButtonClicked,
    clicked_value: string,
  ): void {
    biWidgetInteractionContent({
      button_clicked,
      clicked_value,
      ...this.commonEventData,
      orig_page_name: this.origPageName,
      orig_platform: this.origPlatform,
      solution_info: null,
    });
  }

  logCopyEmailButtonClicked(): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      ...this.kbWidgetStateBI,
      button_clicked: ButtonClicked.CopyEmail,
      solution_info: null,
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
    });
  }

  logOpenEmailButtonClicked(): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      ...this.kbWidgetStateBI,
      button_clicked: ButtonClicked.OpenEmail,
      solution_info: null,
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
    });
  }

  private get currentArticleId(): string | undefined {
    return this._currentPresentedState === PresentedState.SingleArticle
      ? this._lastArticleId
      : undefined;
  }

  private get currentCategoryId(): string | undefined {
    if (this._currentPresentedState === PresentedState.SolutionList) {
      return this._categoryArticlesList?.[0]?.categoryId;
    } else if (
      this._currentPresentedState === PresentedState.SubCategoriesList
    ) {
      return this._categoriesList?.[0]?.parentId;
    }
    return undefined;
  }

  private get commonEventData(): CommonEventsData {
    const defaultState = PresentedState.AllHelpTopics;
    return {
      uuid: userId,
      lng: locale,
      date_created: Date.now(),
      searched_text: this._searchedText,
      last_conversation_id: this._conversationId,
      last_chatbot_node_id: this._nodeId,
      last_user_intent_id: this._intentId,
      ticket_id: this._ticketId,
      current_presented_state: this._currentPresentedState ?? defaultState,
      previous_presented_state: this._previousPresentedState,
      ...getTestVersionPayload(),
    };
  }

  private get kbWidgetStateBI(): KbWidgetStateBI {
    if (!homepageService.homepageEnabled || !helpCenterService.isFullWidth) {
      return {};
    }

    if (this._fullPageSidebarExtended) {
      return { kb_widget_state: KB_WIDGET_STATE.FULL_PAGE_EXTENDED };
    }

    if (this._fullPageSidebarOpened) {
      return { kb_widget_state: KB_WIDGET_STATE.FULL_PAGE_OPENED };
    }

    return {};
  }

  private isCategoriesState(): boolean {
    return [
      PresentedState.AllHelpTopics,
      PresentedState.SubCategoriesList,
    ].includes(this._currentPresentedState!);
  }

  /**
   * Logs content presented in widget except homepage, which has specific `logHomePagePresented`
   * method and requires more input data.
   * Consider splitting this method into smaller ones and moving to feature-driven architecture.
   */
  logPresentedContent(
    solution: SolutionInfoParameters,
    previousPresentedState: PresentedState,
  ) {
    this.previousPresentedState = previousPresentedState;
    switch (this._currentPresentedState) {
      case PresentedState.AllHelpTopics:
        this.logCategoriesPresented(this._categoriesList!);
        return;
      case PresentedState.SubCategoriesList:
      case PresentedState.SolutionList:
        if (this._categoriesList) {
          this.logCategoriesPresented(this._categoriesList);
        } else {
          this.logCategorySolutionListPresented(this._categoryArticlesList!);
        }
        return;
      case PresentedState.Homepage:
        // Skip logging because Home component is responsible for firing this event
        // logHomePagePresented should be used for Home
        return;
      default:
        biWidgetPresentedContent({
          ...this.commonEventData,
          article_id: isString(solution?.articleData)
            ? solution.articleData
            : null,
          solution_info: getSolutionInfo(solution),
          orig_page_name: this._origPageName,
          orig_platform: this._origPlatform,
          current_presented_state: blockNames[
            solution.blockId
          ] as PresentedState,
          article_recommendation_version: this._articleRecommendationVersion,
        });
    }
  }

  logClickOnSolution = (solutionType: SolutionTypes, solutionId: string) => {
    clickOnSolution({
      solution_type: solutionType,
      solution_id: solutionId,
      orig_page_name: this._origPageName,
      orig_platform: this._origPlatform,
      intent_id: this._intentId,
      current_presented_state: this._currentPresentedState!,
      lng: locale,
      current_page_name: document.title,
    });
  };

  logReportABugInteraction({
    positionIndex,
    text,
  }: {
    positionIndex: number;
    text: string;
  }): void {
    biWidgetInteractionContent({
      ...this.commonEventData,
      button_clicked: ButtonClicked.ShareFeedback,
      orig_page_name: this.origPageName,
      orig_platform: this.origPlatform,
      solution_info: {
        text,
        block_id: BlockId.ReportABugButton,
        block_name: blockNames[BlockId.ReportABugButton],
        block_position: positionIndex + 1,
        block_type: BlockType.Button,
        block_details: [
          {
            support_type: SupportType.Button,
            support_position: 1,
            support_id: null,
          },
        ],
      },
    });
  }
}

export const biHelpCenter = new BIHelpCenter();
